import { cameraHasSmartMotionEnabled } from './analytics.new';

interface CameraAccessInfo {
    access?: string;
    analyticsEnabled?: boolean | number | null;
    analyticsScheme?: string | string[] | null;
}

export interface SmartMotionAccessChangedDetail {
    uid: string;
    hasAccess: boolean;
}

// Name of the custom event dispatched whenever the cached value changes.
// Listen for this instead of the native 'storage' event - 'storage' only fires
// for changes made in *other* tabs/windows, never for same-tab writes.
export const SMART_MOTION_ACCESS_CHANGED_EVENT = 'smart-motion-access-changed';

/**
 * sessionStorage key used to cache whether a given user has access to at least
 * one camera with smart motion enabled (used to decide whether to show the
 * Alerts nav link).
 */
export const getSmartMotionAlertsCacheKey = (uid?: string | null): string | null =>
    uid ? `alerts-nav-access-${uid}` : null;

/**
 * Given the full set of cameras a user has access to (as returned by
 * `getAllCameras()`), work out whether at least one of them has smart motion
 * enabled and the user has viewing access to it.
 *
 * @param cameras Object keyed by uidd/deviceId, as returned by getAllCameras().
 */
export const computeSmartMotionAccess = (
    cameras: Record<string, CameraAccessInfo> | null | undefined
): boolean =>
    Object.values(cameras ?? {}).some(
        ({ access, analyticsEnabled, analyticsScheme }) =>
            !!access &&
            ['a', 'r'].includes(access) &&
            cameraHasSmartMotionEnabled(analyticsEnabled, analyticsScheme)
    );

/**
 * Read the cached smart motion alerts access flag for a user, if known.
 *
 * @returns true/false if cached, null if not yet known.
 */
export const readCachedSmartMotionAccess = (uid?: string | null): boolean | null => {
    const cacheKey = getSmartMotionAlertsCacheKey(uid);

    if (!cacheKey) {
        return null;
    }

    const cachedValue = sessionStorage.getItem(cacheKey);

    if (cachedValue === '0' || cachedValue === '1') {
        return cachedValue === '1';
    }

    return null;
};

/**
 * Recompute smart motion alerts access from a full set of cameras and update
 * the cache. Only writes to storage (and notifies listeners) when the value
 * has actually changed, so this is cheap to call opportunistically from
 * anywhere that already has the full camera list handy (e.g. every time
 * `getAllCameras()` is called).
 *
 * Callers MUST pass the full, unfiltered list of cameras the user has access
 * to - a partial list (e.g. a single camera, or another user's cameras) will
 * produce an incorrect result.
 *
 * @returns The freshly computed value, or null if there's no uid to cache against.
 */
export const updateSmartMotionAccessCache = (
    uid: string | null | undefined,
    cameras: Record<string, CameraAccessInfo> | null | undefined
): boolean | null => {
    const cacheKey = getSmartMotionAlertsCacheKey(uid);

    if (!cacheKey || !uid) {
        return null;
    }

    const hasAccess = computeSmartMotionAccess(cameras);
    const previousValue = readCachedSmartMotionAccess(uid);

    sessionStorage.setItem(cacheKey, hasAccess ? '1' : '0');

    if (previousValue !== hasAccess) {
        window.dispatchEvent(
            new CustomEvent<SmartMotionAccessChangedDetail>(SMART_MOTION_ACCESS_CHANGED_EVENT, {
                detail: { uid, hasAccess }
            })
        );
    }

    return hasAccess;
};
